import React, { useEffect, useState, useRef } from "react";
import { Button, Form, ListGroup, Spinner, Badge } from "react-bootstrap";
import { messagesService } from "../../services/messagesService";

const AdminUserChat = ({ userId, height = 260 }) => {
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    messagesService
      .getChatByUser(userId)
      .then((data) => {
        setChatId(data.chatId || null);
        setMessages(data.messages || []);
        isFirstLoad.current = true; // Сбрасываем флаг при смене пользователя
      })
      .finally(() => setLoading(false));
  }, [userId]);

  // Псевдо‑realtime: пока открыт чат и есть chatId, периодически подтягиваем новые сообщения
  useEffect(() => {
    if (!chatId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const fresh = await messagesService.getChatMessagesById(chatId);
        if (!cancelled) {
          setMessages(fresh || []);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    const intervalId = setInterval(poll, 4000); // 1 раз в 4 секунды

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [chatId]);

  useEffect(() => {
    if (
      messages.length > 0 &&
      isFirstLoad.current &&
      messagesContainerRef.current
    ) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
      isFirstLoad.current = false;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      if (chatId) {
        await messagesService.sendAdminMessage(chatId, text.trim());
        const fresh = await messagesService.getChatMessagesById(chatId);
        setMessages(fresh || []);
      } else {
        const started = await messagesService.sendAdminFirstMessageToUser(
          userId,
          text.trim(),
        );
        setChatId(started?.chatId || null);
        setMessages(started?.messages || []);
      }
      setText("");
      // После отправки сообщения скроллим контейнер
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop =
            messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  if (!userId) return null;

  return (
    <div style={{ borderTop: "1px solid #eee", paddingTop: 8, marginTop: 8 }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold">Чат с пользователем</span>
        {!chatId && (
          <Badge bg="secondary">Нет переписки (пользователь не отвечал)</Badge>
        )}
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <>
          <div
            ref={messagesContainerRef}
            style={{
              maxHeight: height,
              minHeight: 120,
              overflowY: "auto",
              border: "1px solid #eee",
              borderRadius: 8,
              padding: 8,
              background: "#fafafa",
            }}
          >
            <ListGroup variant="flush" className="small">
              {messages.map((m) => {
                // Sequelize кладёт связанного пользователя в поле `User`, а не `user`
                const sender = m.User || m.user || {};
                const isAdmin = sender?.username === "connect_admin_system";
                return (
                  <ListGroup.Item
                    key={m.message_id}
                    className="py-1 px-2"
                    style={{
                      border: "none",
                      textAlign: isAdmin ? "right" : "left",
                    }}
                  >
                    <div
                      className={`d-inline-block px-2 py-1 rounded ${
                        isAdmin ? "bg-primary text-white" : "bg-light"
                      }`}
                    >
                      <div className="small mb-1">
                        {isAdmin
                          ? "Админ"
                          : sender?.name || sender?.username || "Пользователь"}
                      </div>
                      <div>{m.content}</div>
                    </div>
                  </ListGroup.Item>
                );
              })}
              <div ref={messagesEndRef} />
            </ListGroup>
          </div>
          <Form onSubmit={handleSend} className="mt-2">
            <div className="d-flex gap-2">
              <Form.Control
                size="sm"
                placeholder="Сообщение пользователю..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={sending}
              />
              <Button
                type="submit"
                size="sm"
                variant="primary"
                disabled={sending || !text.trim()}
              >
                {sending ? "Отправка…" : "Отправить"}
              </Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
};

export default AdminUserChat;
